import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Play, RotateCcw, Volume2, VolumeX, Share2 } from 'lucide-react';
import { W, H, DURATION, createState, step, stepFx, finale, draw, multiplier } from './skyCatchEngine';
import { tierFor, nextTier } from './tiers';
import { ShareScoreModal } from './ShareScoreModal';
import { useSfx } from './useSfx';

const Legend = () => (
  <div className="flex justify-center gap-4 text-[11px] font-mono uppercase tracking-wider text-slate-300 mt-4">
    <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded-full bg-sky-400 inline-block" /> +10 × combo</span>
    <span className="flex items-center gap-1.5"><i className="w-3 h-3 rotate-45 bg-yellow-400 inline-block" /> +100</span>
    <span className="flex items-center gap-1.5"><span className="text-sm leading-none">💣</span> −50</span>
  </div>
);

const TierBadge = ({ score }) => {
  const tier = tierFor(score);
  const next = nextTier(score);
  return (
    <div className="mt-3">
      <div data-testid="game-tier" className="inline-block rounded-full px-4 py-1 font-mono font-bold uppercase text-sm text-[#0B1020]" style={{ background: tier.color }}>{tier.title}</div>
      {next && <div className="text-xs text-slate-400 mt-2">{next.min - score} more for {next.title}</div>}
    </div>
  );
};

const Overlay = ({ phase, score, bestStreak, onStart, onShare }) => (
  <div data-testid={`game-overlay-${phase}`} className="absolute inset-0 flex flex-col items-center justify-center bg-[#0B1020]/85 backdrop-blur-sm text-white text-center px-6 animate-in fade-in duration-300">
    {phase === 'idle' ? (
      <>
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-lime-400">30 second challenge</div>
        <h3 className="font-mono font-bold text-4xl mt-3">SKY CATCH</h3>
        <p className="text-sm text-slate-300 mt-3 max-w-[240px]">Drag or use ← → to move. Catch the blue orbs and gold stars, dodge the bombs.</p>
      </>
    ) : (
      <>
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-lime-400">Time's up</div>
        <div data-testid="game-final-score" className="font-mono font-bold text-6xl mt-3">{score}</div>
        <TierBadge score={score} />
        <div className="text-sm text-slate-300 mt-2">Best streak: {bestStreak}</div>
        <button data-testid="game-share-btn" onClick={onShare}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-[#0B1020] font-mono font-bold uppercase px-5 py-2.5 hover:bg-slate-200 active:scale-95 transition-transform">
          <Share2 size={16} /> Share score
        </button>
      </>
    )}
    <Legend />
    <button data-testid={phase === 'idle' ? 'game-start-btn' : 'game-restart-btn'} onClick={onStart}
      className="mt-6 inline-flex items-center gap-2 rounded-full bg-lime-400 text-[#0B1020] font-mono font-bold uppercase px-6 py-3 hover:bg-lime-300 active:scale-95 transition-transform">
      {phase === 'idle' ? <><Play size={16} /> Start</> : <><RotateCcw size={16} /> Play again</>}
    </button>
  </div>
);

const Hud = ({ hud, muted, onMute }) => (
  <div className="flex items-center justify-between font-mono text-white text-sm mb-2 px-1">
    <div data-testid="game-score">SCORE <b className="text-lime-400 text-lg">{hud.score}</b></div>
    <div data-testid="game-combo" className="text-sky-300">x{multiplier(hud.streak)}</div>
    <div data-testid="game-timer" className={hud.time <= 5 ? 'text-rose-400 animate-pulse' : ''}>{Math.ceil(hud.time)}s</div>
    <button data-testid="game-mute-btn" onClick={onMute} aria-label="Toggle sound" className="opacity-70 hover:opacity-100">
      {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
    </button>
  </div>
);

const SkyCatch = ({ onFinish }) => {
  const canvasRef = useRef(null);
  const stateRef = useRef(createState());
  const [phase, setPhase] = useState('idle');
  const [hud, setHud] = useState({ score: 0, streak: 0, time: DURATION });
  const [muted, setMuted] = useState(false);
  const [sharing, setSharing] = useState(false);
  const sfx = useSfx(!muted);

  useEffect(() => {
    const c = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    c.width = W * dpr; c.height = H * dpr;
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    draw(ctx, stateRef.current);
  }, []);

  useEffect(() => {
    if (phase !== 'playing') return undefined;
    const ctx = canvasRef.current.getContext('2d');
    let raf; let last = performance.now();
    const loop = (t) => {
      const s = stateRef.current;
      step(s, Math.min(0.05, (t - last) / 1000), sfx);
      last = t;
      draw(ctx, s);
      setHud({ score: s.score, streak: s.streak, time: s.timeLeft });
      if (s.timeLeft <= 0) { finale(s, sfx); setPhase('ending'); return; }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [phase, sfx, onFinish]);

  useEffect(() => {
    if (phase !== 'ending') return undefined;
    const ctx = canvasRef.current.getContext('2d');
    let raf; let last = performance.now(); let left = 0.9;
    const loop = (t) => {
      const dt = Math.min(0.05, (t - last) / 1000);
      last = t; left -= dt;
      stepFx(stateRef.current, dt);
      draw(ctx, stateRef.current);
      if (left <= 0) { setPhase('over'); onFinish?.(stateRef.current.score); return; }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [phase, onFinish]);

  useEffect(() => {
    const onKey = (e) => {
      const s = stateRef.current;
      if (e.key === 'ArrowLeft') s.targetX = Math.max(38, s.targetX - 40);
      if (e.key === 'ArrowRight') s.targetX = Math.min(W - 38, s.targetX + 40);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onPointer = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    stateRef.current.targetX = Math.max(38, Math.min(W - 38, x));
  }, []);

  const start = () => {
    stateRef.current = createState();
    setHud({ score: 0, streak: 0, time: DURATION });
    setPhase('playing');
  };

  return (
    <div data-testid="sky-catch-game" className="mx-auto w-full max-w-[400px] rounded-3xl bg-[#0B1020] p-3 shadow-2xl ring-1 ring-white/10 select-none">
      <Hud hud={hud} muted={muted} onMute={() => setMuted((m) => !m)} />
      <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: `${W} / ${H}` }}>
        <canvas ref={canvasRef} data-testid="game-canvas" onPointerMove={onPointer} onPointerDown={onPointer}
          className="w-full h-full block" style={{ touchAction: 'none' }} />
        {(phase === 'idle' || phase === 'over') && <Overlay phase={phase} score={hud.score} bestStreak={stateRef.current.bestStreak} onStart={start} onShare={() => setSharing(true)} />}
      </div>
      {sharing && <ShareScoreModal score={hud.score} bestStreak={stateRef.current.bestStreak} onClose={() => setSharing(false)} />}
    </div>
  );
};

export default SkyCatch;
